import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type { Pagina, ServicioReparacion } from '../api/tipos';
import { Cargando, Dialogo, Error, Insignia, Paginacion } from '../componentes/comunes';

/**
 * Catálogo de servicios de reparación.
 *
 * Vive en Compras porque es quien negocia con los técnicos y sabe lo que
 * cuesta cada trabajo. De aquí salen los renglones de la cotización que
 * se registra en la orden: quien captura elige un servicio y los precios
 * aparecen solos.
 *
 * Los precios son de referencia, no una tarifa. Al cotizar se proponen y
 * se corrigen en esa orden, porque el mismo trabajo no cuesta igual en
 * Chiapas que en Monterrey. Corregir uno aquí no cambia lo ya cotizado:
 * cada renglón guardó su propia copia.
 */
export default function ServiciosReparacion({ puede }: { puede: (m: string, a: string) => boolean }) {
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState('activo');
  const [numero, setNumero] = useState(1);

  const [pagina, setPagina] = useState<Pagina<ServicioReparacion> | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<ServicioReparacion | null>(null);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<ServicioReparacion>>('/servicios-reparacion', {
      busqueda: busqueda || undefined,
      estado: estado || undefined,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [busqueda, estado, numero]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  return (
    <div className="panel">
      <div className="aviso" style={{ marginBottom: 14 }}>
        Los precios son de referencia: al registrar la cotización de una
        orden se proponen y se pueden corregir ahí. Cambiarlos aquí no
        toca lo ya cotizado.
      </div>

      <div className="controles">
        <input
          type="search"
          placeholder="Código o descripción…"
          value={busqueda}
          onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
        />
        <select value={estado} onChange={(e) => { setEstado(e.target.value); setNumero(1); }}>
          <option value="activo">Activos</option>
          <option value="inactivo">Inactivos</option>
          <option value="">Todos</option>
        </select>
        {puede('compras', 'crear') && (
          <button className="primario" onClick={() => setCreando(true)}>Nuevo servicio</button>
        )}
      </div>

      <Error error={error} />

      {cargando ? <Cargando /> : (
        <>
          {pagina?.data.length === 0 ? (
            <div className="vacio">No hay servicios que cumplan esa búsqueda.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descripción</th>
                  <th className="numero">Costo</th>
                  <th className="numero">Venta</th>
                  <th></th>
                  <th className="acciones"></th>
                </tr>
              </thead>
              <tbody>
                {pagina?.data.map((s) => (
                  <tr key={s.id}>
                    <td className="mono">{s.codigo}</td>
                    <td>{s.descripcion}</td>
                    <td className="numero">{dinero(s.precioCosto)}</td>
                    <td className="numero">{dinero(s.precioVenta)}</td>
                    <td>{!s.activo && <Insignia activo={false} />}</td>
                    <td className="acciones">
                      {puede('compras', 'editar') && (
                        <button onClick={() => setEditando(s)}>Editar</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {pagina && (
            <Paginacion
              pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
              onCambiar={setNumero}
            />
          )}
        </>
      )}

      {(creando || editando) && (
        <FormularioServicio
          servicio={editando}
          onCerrar={() => { setCreando(false); setEditando(null); }}
          onGuardado={() => { setCreando(false); setEditando(null); cargar(); }}
        />
      )}
    </div>
  );
}

/** Sin precio pactado no se escribe cero: cero diría que es gratis. */
function dinero(valor: number | null): string {
  if (valor === null) return '—';
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function FormularioServicio({
  servicio, onCerrar, onGuardado,
}: {
  servicio: ServicioReparacion | null;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const esAlta = !servicio;

  const [datos, setDatos] = useState({
    codigo: servicio?.codigo ?? '',
    descripcion: servicio?.descripcion ?? '',
    precioCosto: servicio?.precioCosto === null || servicio === null
      ? '' : String(servicio.precioCosto),
    precioVenta: servicio?.precioVenta === null || servicio === null
      ? '' : String(servicio.precioVenta),
    /**
     * Qué propone este concepto para la casilla del renglón.
     *
     * Tres valores y por eso es un select y no una casilla: «' '» es «no
     * opina», que es lo que vale todo lo capturado antes de la 0076 y
     * lo que deja que conteste la matriz de cobertura. Una casilla
     * desmarcada afirmaría que no entra en la iguala, que es una
     * respuesta que nadie dio.
     */
    entraEnIguala: servicio?.entraEnIguala === true ? 'si'
      : servicio?.entraEnIguala === false ? 'no' : '',
    activo: servicio?.activo ?? true,
  });
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  const listo = datos.descripcion.trim().length >= 3
    && (esAlta ? datos.codigo.trim().length >= 2 : true);

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      // Vacío se manda como null y no como cero: es «no se ha pactado».
      const cuerpo: Record<string, unknown> = {
        descripcion: datos.descripcion.trim(),
        precioCosto: datos.precioCosto === '' ? null : Number(datos.precioCosto),
        precioVenta: datos.precioVenta === '' ? null : Number(datos.precioVenta),
        entraEnIguala: datos.entraEnIguala === '' ? null : datos.entraEnIguala === 'si',
      };

      if (esAlta) {
        cuerpo.codigo = datos.codigo.trim();
        await api.post('/servicios-reparacion', cuerpo);
      } else {
        cuerpo.activo = datos.activo;
        await api.patch(`/servicios-reparacion/${servicio!.id}`, cuerpo);
      }

      onGuardado();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={esAlta ? 'Nuevo servicio' : `Editar ${servicio!.codigo}`}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={trabajando || !listo}>
            {trabajando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <Error error={error} />

      {esAlta && (
        <div className="campo">
          <label htmlFor="codigo">Código *</label>
          <input
            id="codigo" className="mono" value={datos.codigo} maxLength={30}
            onChange={(e) => setDatos({ ...datos, codigo: e.target.value.toUpperCase() })}
          />
          <span className="ayuda">
            No se puede cambiar después: es lo que queda copiado en cada
            cotización que lo use.
          </span>
        </div>
      )}

      <div className="campo">
        <label htmlFor="descripcion">Descripción *</label>
        <input
          id="descripcion" value={datos.descripcion} maxLength={300}
          onChange={(e) => setDatos({ ...datos, descripcion: e.target.value })}
          placeholder="Cambio de compresor de media tonelada"
        />
      </div>

      <div className="rejilla-campos">
        <div className="campo">
          <label htmlFor="precioCosto">Precio costo</label>
          <input
            id="precioCosto" type="number" min="0" step="0.01" value={datos.precioCosto}
            onChange={(e) => setDatos({ ...datos, precioCosto: e.target.value })}
          />
          <span className="ayuda">Lo que se le paga al técnico.</span>
        </div>

        <div className="campo">
          <label htmlFor="precioVenta">Precio venta</label>
          <input
            id="precioVenta" type="number" min="0" step="0.01" value={datos.precioVenta}
            onChange={(e) => setDatos({ ...datos, precioVenta: e.target.value })}
          />
          <span className="ayuda">Lo que se le cobra al cliente.</span>
        </div>
      </div>

      <div className="aviso">
        Dejarlos vacíos es válido y significa que no se han pactado, que no
        es lo mismo que cero.
      </div>

      {/*
        Lo que este concepto propone sobre quién lo paga.
        No decide: propone. La matriz de cobertura —área × formato de
        tienda— le gana porque es más específica, y quien captura la
        cotización le gana a las dos porque es el único que sabe lo que
        pasó en sitio. Esto sólo es el valor con el que llega marcada la
        casilla del renglón, para no tener que marcarla siempre a mano.
      */}
      <div className="campo">
        <label htmlFor="entraEnIguala">¿Lo cubre la iguala, normalmente?</label>
        <select
          id="entraEnIguala" value={datos.entraEnIguala}
          onChange={(e) => setDatos({ ...datos, entraEnIguala: e.target.value })}
        >
          <option value="">No lo dice: que decida la matriz de cobertura</option>
          <option value="si">Sí, normalmente lo absorbe la iguala</option>
          <option value="no">No, normalmente se le cobra al cliente</option>
        </select>
        <span className="ayuda">
          Con esto llega marcada la casilla «Lo cubre la iguala» del renglón
          al cotizar. La matriz de cobertura manda sobre esto cuando tiene
          respuesta para el área y el formato de la tienda, y quien cotiza
          manda sobre las dos.
        </span>
      </div>

      {!esAlta && (
        <div className="campo">
          <label htmlFor="activo">
            <input
              id="activo" type="checkbox" checked={datos.activo}
              onChange={(e) => setDatos({ ...datos, activo: e.target.checked })}
            />
            {' '}Activo
          </label>
          <span className="ayuda">
            Desactivarlo lo saca del desplegable de la cotización. Las que
            ya lo usaron no cambian.
          </span>
        </div>
      )}
    </Dialogo>
  );
}
